import {computed, inject, Injectable, signal} from '@angular/core';
import {AnalyticService, ApiReportAnalyticPeriodsParams, Period} from "../../apiModule1";
import {map, pairwise, switchMap} from "rxjs/operators";
import {BehaviorSubject, distinctUntilChanged, filter, tap} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class PeriodColumnsService {
    analyticsService = inject(AnalyticService)

    private initialPeriods = signal<Period[]>([])

    private periods = signal<Period[]>([]) //сортированный массив периодов
    private selectedPeriod = signal<Period[]>([]) //выбранные периоды

    public selectedPeriodString = computed<string[]>(() => this.selectedPeriod().map(period => `${period.quarter ? period.quarter + ' кв. ' : ''}${period.year}`))

    filters$ = new BehaviorSubject<ApiReportAnalyticPeriodsParams>({})

    constructor() {
        //начальные данные
        this.analyticsService.apiRatingAnalyticPeriods({})
            .pipe(map(v => v.values))
            .subscribe({
                next: (v: Period[]) => {
                    this.initialPeriods.set(v)
                    this.setPeriods(v)
                }
            })


        this.filters$
            .pipe(
                pairwise(),
                tap(([prev, curr])=>{
                    console.log('prev, curr', prev, curr)
                }),
                filter(([prev, curr]) => !!Object.keys(prev).length && prev!==null),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                switchMap(([_,filters] )=> this.analyticsService.apiRatingAnalyticPeriods(filters)),
                map(v => v.values)
            )
            .subscribe({
                next: (v: Period[]) => {
                    this.setPeriods(v);
                    this.changeSelectedAfterNewFilter()
                }
            });
    }

    changeSelectedAfterNewFilter(){
        const newSelected = this.selectedPeriod().filter(selected => this.periods().some(period => period.year === selected.year && period.quarter === selected.quarter))
        this.selectedPeriod.set([])
        newSelected.forEach(period => {
            this.addToSelectedPeriod(period)
        })
    }

    getInitialPeriods() {
        if (this.initialPeriods().length) {
            return this.initialPeriods()
        } else {
            return []
        }
    }

    getInitialStringArraySignal = computed(() => this.initialPeriods().map(period => `${period.quarter ? period.quarter + ' кв. ' : ''}${period.year}`))
    getSelectedStringArraySignal = computed(() => this.selectedPeriod().map(period => `${period.quarter ? period.quarter + ' кв. ' : ''}${period.year}`))

    setPeriods(periods: Period[]) {
        this.periods.set(this._sortPeriods(periods))
    }

    setFilters(filters: ApiReportAnalyticPeriodsParams) {
        if (!filters) return
        this.filters$.next(filters)
    }

    getPeriodsValue() {
        return this.periods()
    }


    getPeriodsSignal() {
        return this.periods
    }

    getStringArraySignal = computed(() => this.periods().map(period => `${period.quarter ? period.quarter + ' кв. ' : ''}${period.year}`))


    private _sortPeriods(periods: Period[]): Period[] {
        const res = periods.toSorted((a: Period, b: Period) => {
            if (!a || !b || !a.year || !b.year) return -1;
            const aQuarter = a.year * 4 + (a.quarter ?? 4.5);
            const bQuarter = b.year * 4 + (b.quarter ?? 4.5);
            return bQuarter - aQuarter
        })
        return res
    }

    getStringArray(): string[] {
        return this.getStringArraySignal()
    }

    parsePeriodString(periodString: string): Period {
        if (!periodString) {
            return {year: null, quarter: null}
        }
        const periodArray = periodString.split(' кв. ')
        if (periodArray.length === 1) {
            return {
                year: +periodArray[0],
                quarter: null
            }
        } else {
            return {
                year: +periodArray[1],
                quarter: +periodArray[0]
            }
        }
    }

    private _gaugePeriod(period: Period | string): {
        year: number | null
        quarter: number | null
    } | undefined
    {
        let workingPeriod = null
        if (typeof period === 'string') {
            workingPeriod = {...this.parsePeriodString(period)}
        } else {
            workingPeriod = {...period}
        }
        if (!workingPeriod || !this.periods().find(e => e.year === workingPeriod.year && e.quarter === workingPeriod.quarter)) return
        return workingPeriod
    }

    addToSelectedPeriod(period: Period | string) {
        if (this.isSelected(period)) return //если уже есть, то не добавляем, чтобы не было дублирования в массиве выбранных периодов. Если нужно дублировать, то убрать эту строку и добавить в условие ниже else, чтобы добавлялось дублирование в массив

        //если нет периодов, то ждем пока загрузятся
        if(!this.periods().length){
            const interval = setInterval(()=>{
                if(this.periods().length){
                    const workingPeriod = this._gaugePeriod(period)
                    if (workingPeriod) this.selectedPeriod.update(value => this._sortPeriods([...value, workingPeriod]))
                    clearInterval(interval)
                }
            }, 200)
            return
        }

        const workingPeriod = this._gaugePeriod(period)
        if (!workingPeriod) return

        //добавляем
        this.selectedPeriod.update(value => this._sortPeriods([...value, workingPeriod]))
    }

    clearSelectedPeriod() {
        this.selectedPeriod.set([])
    }

    selectPeriod(period: Period | string) {
        const workingPeriod = this._gaugePeriod(period)
        if (!workingPeriod) return

        //удаляем если уже есть
        if (this.isSelected(workingPeriod)) {
            const newSelected = (value: Period[]) => {
                const res = value.filter(e => !(e.year === workingPeriod.year && e.quarter === workingPeriod.quarter))
                return res
            }
            return this.selectedPeriod.update(newSelected)
        }

        //добавляем
        this.selectedPeriod.update(value => [...value, workingPeriod])
        console.log('this.selectedPeriod()', this.selectedPeriod())
    }

    deleteSelectedPeriod(period: Period | string) {
        let workingPeriod = null
        if (typeof period === 'string') {
            workingPeriod = this.parsePeriodString(period)
        } else {
            workingPeriod = {
                year: period.year,
                quarter: period.quarter
            }
        }
        if (this.isSelected(workingPeriod)) {
            const newSelected = (value: Period[]) => {
                const res = value.filter(e => !(e.year === workingPeriod.year && e.quarter === workingPeriod.quarter))
                return res
            }
            this.selectedPeriod.update(newSelected)
        }
    }

    getSelectedValue() {
        return this.selectedPeriod()
    }

    getSelectedSignal() {
        return this.selectedPeriod
    }

    isSelected(period: Period | string) {
        if (!period) {
            return false
        }
        if (typeof period === 'string') {
            return this.selectedPeriod().find(e => e.year === this.parsePeriodString(period).year
                && e.quarter === this.parsePeriodString(period).quarter
            )
        } else {
            return this.selectedPeriod().find(e => e.year === period.year
                && e.quarter === period.quarter
            )
        }
    }
}
